// Pure billing decision logic (no network). Kept separate from billingApi.ts so
// it can be unit-tested without mocking fetch/axios.

import type { ApplicantPaymentInfo } from './billingApi'

// ─── Applicant fee step ─────────────────────────────────────────────

/**
 * Checkout return state. The callback redirects with `payment=success|failed`
 * and always `tx_ref`; failures carry `reason`. `reason=status_pending` (e.g.
 * mobile money not confirmed yet) means the webhook may still complete the
 * payment, so it is reported as 'pending', not 'failed'.
 */
export type PaymentReturnStatus = 'success' | 'failed' | 'pending' | null

/** Read `?payment=…&reason=…` from a query string / URLSearchParams. */
export function readPaymentReturn(params: URLSearchParams | string): PaymentReturnStatus {
  const p = typeof params === 'string' ? new URLSearchParams(params) : params
  const v = (p.get('payment') || '').toLowerCase()
  const reason = (p.get('reason') || '').toLowerCase()
  if (v === 'success' || v === 'successful' || v === 'completed') return 'success'
  if (v === 'pending' || reason === 'status_pending') return 'pending'
  if (v === 'failed' || v === 'cancelled' || v === 'canceled' || v === 'error') return 'failed'
  return null
}

/** The failure `reason` from the return URL, if any. */
export function readPaymentReason(params: URLSearchParams | string): string | null {
  const p = typeof params === 'string' ? new URLSearchParams(params) : params
  return p.get('reason')
}

/** Applicant-friendly text for a failure reason. */
export function paymentFailureText(reason: string | null): string {
  switch (reason) {
    case 'cancelled': return 'The payment was cancelled. You can try again.'
    case 'amount_mismatch': case 'currency_mismatch': return "The payment amount didn't match the fee, so it wasn't accepted. Please try again."
    case 'status_failed': return 'The payment was declined. Please try another card or mobile money account.'
    default: return "The payment didn't go through. No fee was taken — please try again."
  }
}

export type PaymentStep =
  /** Still waiting for GET /payment. */
  | 'checking'
  /** No fee, or already paid — run the normal verification flow. */
  | 'clear'
  /** Fee required and unpaid — show the "Verification fee" card. */
  | 'pay'
  /** Returned from checkout with success but the server hasn't recorded it yet — poll again. */
  | 'confirming'

export interface PaymentDecision {
  step: PaymentStep
  /** Inline notice shown on the fee card. */
  notice: 'payment_failed' | 'payment_pending' | null
}

export interface PaymentDecisionInput {
  /** Response of GET /api/v2/verify/:id/payment; null while loading. */
  payment: ApplicantPaymentInfo | null
  /** The GET failed (network / 5xx). We never block the applicant on our own outage. */
  fetchFailed?: boolean
  /** `?payment=` on the return URL. */
  returnStatus?: PaymentReturnStatus
  /** How many confirmation re-checks have already run after a success return. */
  confirmAttempts?: number
  /** Max re-checks (after a success/pending return) before falling back to the pay card with a "pending" notice. */
  maxConfirmAttempts?: number
}

/**
 * Decide what the hosted verification page should show for the applicant fee.
 *
 * - Fee not required, or paid → 'clear' (the existing flow is untouched).
 * - Fetch failed → 'clear': uploads are still gated server-side (402
 *   payment_required), which routes back to the fee step, so failing open here
 *   never lets an unpaid applicant through.
 * - Required + unpaid after a `payment=success` return → 'confirming' while
 *   re-checks remain (callback → webhook race), then 'pay' + pending notice.
 * - Required + unpaid → 'pay' (with a failure notice after `payment=failed`).
 */
export function decidePaymentStep(input: PaymentDecisionInput): PaymentDecision {
  const { payment, fetchFailed, returnStatus = null, confirmAttempts = 0, maxConfirmAttempts = 4 } = input
  if (fetchFailed) return { step: 'clear', notice: null }
  if (!payment) return { step: 'checking', notice: null }
  // `required` stays true after payment — always gate on required && !paid.
  if (!payment.required || payment.paid) return { step: 'clear', notice: null }
  if (returnStatus === 'success' || returnStatus === 'pending') {
    if (confirmAttempts < maxConfirmAttempts) return { step: 'confirming', notice: null }
    return { step: 'pay', notice: 'payment_pending' }
  }
  if (returnStatus === 'failed') return { step: 'pay', notice: 'payment_failed' }
  return { step: 'pay', notice: null }
}

/** True when an upload/route response is the applicant-fee gate (HTTP 402 payment_required). */
export function isPaymentRequiredResponse(status: number, body: any): boolean {
  return status === 402 && (body?.error === 'payment_required' || body?.code === 'payment_required')
}

/** True when an initialize response is the org-wallet gate (HTTP 402 insufficient_balance). */
export function isInsufficientBalanceResponse(status: number | undefined, body: any): boolean {
  return status === 402 && (body?.error === 'insufficient_balance' || body?.code === 'insufficient_balance')
}

/** Basic applicant contact validation for the fee form. */
export function validatePayerDetails(d: { name: string; email: string; phone?: string }): Partial<Record<'name' | 'email' | 'phone', string>> {
  const errors: Partial<Record<'name' | 'email' | 'phone', string>> = {}
  if (d.name.trim().length < 2) errors.name = 'Enter your full name'
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(d.email.trim())) errors.email = 'Enter a valid email address'
  if (d.phone && d.phone.trim() && !/^\+?[\d\s()-]{6,20}$/.test(d.phone.trim())) errors.phone = 'Enter a valid phone number'
  return errors
}

/** Strip the checkout return params (`payment`, `tx_ref`, `transaction_id`, `status`, `reason`), keeping everything else. */
export function stripPaymentReturnParams(search: string): string {
  const p = new URLSearchParams(search)
  ;['payment', 'tx_ref', 'transaction_id', 'status', 'reason'].forEach(k => p.delete(k))
  const s = p.toString()
  return s ? `?${s}` : ''
}

// ─── Organisation top-up ────────────────────────────────────────────

export const TOPUP_PRESETS_CENTS = [1000, 2500, 5000, 10000, 25000]

/** Presets at or above the API minimum (the minimum itself is always offered first). */
export function topupPresets(minCents: number): number[] {
  const min = Math.max(1, Math.round(minCents || 0))
  const list = TOPUP_PRESETS_CENTS.filter(c => c >= min)
  if (!list.includes(min)) list.unshift(min)
  return list.slice(0, 5)
}

/** Validate a custom top-up amount; returns an error message or null. */
export const MAX_TOPUP_CENTS = 10_000_000

export function validateTopup(cents: number | null, minCents: number, maxCents = MAX_TOPUP_CENTS): string | null {
  if (cents === null) return 'Enter an amount in dollars, e.g. 25 or 25.50'
  if (cents < minCents) return `Minimum top-up is $${(minCents / 100).toFixed(2)}`
  if (cents > maxCents) return `Maximum top-up is $${(maxCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
  return null
}

/** How many verifications a balance covers at the current price. */
export function verificationsCovered(balanceCents: number, priceCents: number): number | null {
  if (!priceCents || priceCents <= 0) return null
  return Math.max(0, Math.floor(balanceCents / priceCents))
}
